import {
  EventRecognizers,
  GraphComponent,
  GraphInputMode,
  GraphItemTypes,
  IModelItem,
  ModifierKeys,
  PointerButtons,
  PointerType,
} from '@ardoq/yfiles';
import * as GraphSelection from 'yfilesExtensions/graphSelection';
import { isMac } from '@ardoq/common-helpers';
import { isAggregatedGraphEdge } from '@ardoq/graph';
import { ViewIds } from '@ardoq/api-types';
import { graphItemClickedListenerModernized } from './graphEvents/graphItemClickedListenerModernized';
import { graphItemDoubleClickedListener } from './graphEvents/graphItemDoubleClickedListener';
import { graphViewpointModeItemDoubleClickedListener } from './graphEvents/graphViewpointModeItemDoubleClickedListener';
import { graphItemRightClickedListener } from './graphEvents/graphItemRightClickedListener';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { noop } from 'lodash';
import { GraphEventType } from './graphEvents/types';
import type { GraphEventHandlers } from './graphEvents/types';

const pressedRecognizer = EventRecognizers.createPointerEventRecognizer({
  pointerTypes: [PointerType.MOUSE],
  buttons: PointerButtons.MOUSE_LEFT,
  modifiers: isMac() ? ModifierKeys.META : ModifierKeys.CONTROL,
});

const isSelectable = (item: IModelItem): boolean =>
  item &&
  item.tag &&
  (item.tag.isComponent?.() ||
    item.tag.isReference?.() ||
    isAggregatedGraphEdge(item.tag));

type AddClickHandlersNewArgs = {
  selectableItems: GraphItemTypes;
  focusableItems: GraphItemTypes;
  isViewpointMode: () => boolean;
  isPresentationMode: boolean;
  graphEventHandlers: GraphEventHandlers;
};

/**
 * Graph Event Handlers Configuration for yFiles graph components.
 */
export const addClickHandlers = (
  graphComponent: GraphComponent,
  {
    selectableItems,
    focusableItems,
    isPresentationMode,
    isViewpointMode,
    graphEventHandlers,
  }: AddClickHandlersNewArgs
): void => {
  const inputMode = graphComponent.inputMode as GraphInputMode;

  inputMode.selectableItems =
    inputMode.clickSelectableItems =
    inputMode.marqueeSelectableItems =
      selectableItems;
  inputMode.focusableItems = focusableItems;

  inputMode.marqueeSelectionInputMode.enabled = !isPresentationMode;
  inputMode.marqueeSelectionInputMode.beginRecognizer = pressedRecognizer;

  inputMode.selectablePredicate = isPresentationMode
    ? () => false
    : isSelectable;

  // Register custom click handlers - curry graphComponent into each handler
  inputMode.addEventListener(GraphEventType.ITEM_CLICKED, (e, inputMode) =>
    graphEventHandlers[GraphEventType.ITEM_CLICKED](
      e,
      inputMode,
      graphComponent
    )
  );

  inputMode.addEventListener(
    GraphEventType.ITEM_DOUBLE_CLICKED,
    (e, inputMode) =>
      graphEventHandlers[GraphEventType.ITEM_DOUBLE_CLICKED](
        e,
        inputMode,
        graphComponent
      )
  );

  inputMode.addEventListener(
    GraphEventType.ITEM_RIGHT_CLICKED,
    (e, inputMode) =>
      graphEventHandlers[GraphEventType.ITEM_RIGHT_CLICKED](
        e,
        inputMode,
        graphComponent
      )
  );

  inputMode.addEventListener(GraphEventType.MULTI_SELECTION_FINISHED, () =>
    graphEventHandlers[GraphEventType.MULTI_SELECTION_FINISHED](graphComponent)
  );

  // When a user is in viewpoint mode and a contributor on the new Discover journey,
  // we don't want to allow multi-selection. You should only be able to select one item at a time.
  // Ref. https://kb.yworks.com/article/679/Changing-the-Multi-Selection-Modifier
  if (isViewpointMode() && isDiscoverJourney()) {
    inputMode.multiSelectionRecognizer = () => false;
  }
};

type GetDefaultGraphEventHandlersArgs = {
  viewId: ViewIds | null;
  viewInstanceId: string;
  isViewpointMode: () => boolean;
};
/**
 * Provides handlers that replicate the legacy `addClickHandlers()` behavior which is
 * shared across all graph component views. If you don't provide your own configuration,
 * the default handlers will be used.
 */
export const getDefaultGraphEventHandlers = ({
  viewId,
  viewInstanceId,
  isViewpointMode,
}: GetDefaultGraphEventHandlersArgs): GraphEventHandlers => {
  const handleSelectionChanged =
    viewId === ViewIds.MODERNIZED_BLOCK_DIAGRAM
      ? GraphSelection.handleSelectionChangedModernizedBlockDiagram
      : GraphSelection.handleSelectionChanged;

  return {
    [GraphEventType.ITEM_CLICKED]: (e, inputMode, _graphComponent) =>
      graphItemClickedListenerModernized(viewId, viewInstanceId)(e, inputMode),

    [GraphEventType.ITEM_DOUBLE_CLICKED]: (e, inputMode, _graphComponent) => {
      // In viewpoint mode, use the viewpoint-specific handler
      if (viewId && isViewpointMode()) {
        return graphViewpointModeItemDoubleClickedListener(e, inputMode);
      }
      // Otherwise use the legacy handler
      return graphItemDoubleClickedListener(e, inputMode);
    },

    [GraphEventType.ITEM_RIGHT_CLICKED]: viewId
      ? (e, inputMode, graphComponent) =>
          graphItemRightClickedListener({
            graphComponent,
            isViewpointMode,
            viewId,
            viewInstanceId,
          })(e, inputMode)
      : noop,

    [GraphEventType.MULTI_SELECTION_FINISHED]: handleSelectionChanged,
  };
};
